import { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navigationItems = [
    { to: ROUTE_PATHS.HOME, label: 'Accueil' },
    { to: ROUTE_PATHS.SERVICES, label: 'Services', badge: 'Parents' },
    { to: ROUTE_PATHS.FOR_LAWYERS, label: 'Avocats', badge: 'Professionnels' },
    { to: ROUTE_PATHS.ADOPTION, label: 'Adoption', badge: 'Nouveau' },
    { to: ROUTE_PATHS.ABOUT, label: 'À propos' },
    { to: ROUTE_PATHS.BLOG, label: 'Ressources' },
    { to: ROUTE_PATHS.FAQ, label: 'FAQ' },
    { to: ROUTE_PATHS.CONTACT, label: 'Contact' },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          isScrolled
            ? 'bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-md'
            : 'bg-background'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to={ROUTE_PATHS.HOME} className="flex items-center space-x-3 group">
              <img
                src={IMAGES.LOGO}
                alt="Centre d'expertise familiale de l'Outaouais"
                className="w-12 h-12 object-contain group-hover:scale-105 transition-transform duration-200"
              />
              <div className="hidden md:block">
                <div className="font-semibold text-lg text-foreground leading-tight">
                  Centre d'expertise familiale
                </div>
                <div className="text-sm text-muted-foreground">de l'Outaouais</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative ${
                      isActive
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`
                  }
                >
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-secondary/20 text-secondary-foreground">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center space-x-4">
              <Button asChild className="hidden lg:inline-flex">
                <Link to={ROUTE_PATHS.CONTACT}>Prendre rendez-vous</Link>
              </Button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-foreground" />
                ) : (
                  <Menu className="w-6 h-6 text-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden border-t border-border bg-background"
            >
              <nav className="container mx-auto px-4 py-6 space-y-2">
                {navigationItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `block px-4 py-3 rounded-lg text-base font-medium transition-all ${
                        isActive
                          ? 'text-primary bg-primary/10'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`
                    }
                  >
                    <div className="flex items-center justify-between">
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs rounded-full bg-secondary/20 text-secondary-foreground">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  </NavLink>
                ))}
                <Button asChild className="w-full mt-4">
                  <Link to={ROUTE_PATHS.CONTACT} onClick={() => setIsMobileMenuOpen(false)}>
                    Prendre rendez-vous
                  </Link>
                </Button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-muted/30 border-t border-border mt-24">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src={IMAGES.LOGO}
                  alt="Logo Centre d'expertise familiale"
                  className="w-10 h-10 object-contain"
                />
                <div>
                  <div className="font-semibold text-foreground">Centre d'expertise</div>
                  <div className="text-sm text-muted-foreground">familiale de l'Outaouais</div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Services psychosociaux et familiaux spécialisés en contexte de séparation et de
                coparentalité difficile.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Liens rapides</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to={ROUTE_PATHS.SERVICES}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Nos services
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.FOR_LAWYERS}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Pour les avocats
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.ADOPTION}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Adoption internationale
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.BLOG}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Ressources
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.GUIDES}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Guides pratiques
                  </Link>
                </li>
                <li>
                  <Link
                    to={ROUTE_PATHS.VIDEOS}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Vidéos
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Contact</h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    39 rue Sainte-Marie
                    <br />
                    Gatineau, QC J8Y 4A2
                  </span>
                </li>
                <li className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                  <a
                    href="https://expertisefamiliale.janeapp.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    Prendre rendez-vous
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                  <a
                    href="mailto:info@expertisefamiliale.ca"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    info@expertisefamiliale.ca
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-foreground mb-4">Heures d'ouverture</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <div>Lundi - Vendredi</div>
                    <div className="font-medium text-foreground">9h00 - 17h00</div>
                  </div>
                </li>
                <li className="text-sm text-muted-foreground ml-8">
                  Rendez-vous sur demande
                  <br />
                  en soirée et fin de semaine
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className="text-sm text-muted-foreground text-center md:text-left">
                © 2026 Centre d'expertise familiale de l'Outaouais. Tous droits réservés.
              </p>
              <div className="flex items-center space-x-6">
                <a
                  href="#"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Politique de confidentialité
                </a>
                <a
                  href="#"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Mentions légales
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}